// ────────────────────────────────────────────────────────────────────
// CompliancePanel — DOEB / DIW inspection due-dates (slide 7: "DOEB-ready").
// The compliance record a plant shows an auditor: what's overdue, what's due
// soon, grouped by regulator — with every interval's provenance visible. An
// inferred interval yields a PROVISIONAL due-date (never sold as legal fact);
// staff confirm it against the permit to make it authoritative.
// ────────────────────────────────────────────────────────────────────
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiClient, getCachedUser, STAFF_ROLES } from "@/lib/api";
import type { ComplianceOut, ComplianceRow } from "@/lib/types";

const CRIT = "var(--err)", WARN = "var(--warn)", OK = "var(--ok)", NONE = "#6E7585";

function stateMeta(s: ComplianceRow["state"]): { color: string; label: string } {
  switch (s) {
    case "overdue": return { color: CRIT, label: "OVERDUE" };
    case "due_soon": return { color: WARN, label: "DUE SOON" };
    case "ok": return { color: OK, label: "OK" };
    default: return { color: NONE, label: "NO DATE" };
  }
}

export function CompliancePanel({ plantId }: { plantId: number }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isStaff = (STAFF_ROLES as readonly string[]).includes(getCachedUser()?.role ?? "");
  const q = useQuery({
    queryKey: ["compliance", plantId],
    queryFn: () => apiClient.plantCompliance(plantId),
    staleTime: 60_000,
  });
  const seed = useMutation({
    mutationFn: () => apiClient.seedComplianceDefaults(plantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["compliance", plantId] }),
  });

  const t: ComplianceOut | undefined = q.data;
  if (q.isLoading) return <Center>Loading compliance…</Center>;
  if (!t) return <Center>Compliance unavailable</Center>;

  const s = t.summary;
  const authLine = Object.entries(t.by_authority).map(([a, n]) => `${a} ${n}`).join(" · ") || "—";

  return (
    <div className="absolute inset-0 overflow-auto" style={{ background: "var(--paper)" }}>
      <div className="px-4 pt-3 pb-2 flex items-center justify-between font-mono uppercase border-b"
           style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
        <span>/ Regulatory compliance · DOEB / DIW inspection due-dates</span>
        <span style={{ color: "var(--ink-3)" }}>{authLine}</span>
      </div>

      {/* summary strip */}
      <div className="px-4 py-3 flex flex-wrap gap-2.5">
        <Stat label="OVERDUE" value={s.overdue} color={CRIT} />
        <Stat label="DUE ≤ 6 MO" value={s.due_soon} color={WARN} />
        <Stat label="NO DATE" value={s.unknown} color={NONE} />
        <Stat label="ASSETS" value={s.total} color="var(--ink-2)" />
      </div>

      {/* provisional-interval notice — the honesty line */}
      {s.provisional > 0 && (
        <div className="mx-4 mb-3 px-3 py-2 font-mono flex items-center justify-between gap-3"
             style={{ fontSize: 11, lineHeight: 1.5, color: "var(--warn)",
                      background: "rgba(var(--accent-rgb) / .09)", border: "1px solid rgba(var(--accent-rgb) / .4)", borderRadius: 7 }}>
          <span>▲ {s.provisional} รอบตรวจเป็นค่า <b>inferred</b> (ทั่วไปตาม API/ASME) — due-date ยัง provisional จนกว่าจะยืนยันกับ permit</span>
          {isStaff && (
            <button onClick={() => seed.mutate()} disabled={seed.isPending}
                    className="font-mono whitespace-nowrap px-2.5 py-1 border transition-colors"
                    style={{ fontSize: 10, letterSpacing: ".08em", color: "var(--warn)",
                             borderColor: "rgba(var(--accent-rgb) / .5)", borderRadius: 5, background: "transparent" }}>
              {seed.isPending ? "…" : "SEED DEFAULTS"}
            </button>
          )}
        </div>
      )}

      {/* rows — worst first */}
      <div className="px-4 pb-6">
        <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--ink-4)" }}>
              <Th>Asset</Th><Th>Authority</Th><Th>Interval</Th><Th>Last insp.</Th><Th>Next due</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {t.rows.map((r) => {
              const m = stateMeta(r.state);
              return (
                <tr key={r.asset_id} className="cursor-pointer transition-colors hover:bg-[rgba(10,22,40,0.03)]"
                    onClick={() => navigate(`/assets/${r.asset_id}`)}
                    style={{ borderTop: "1px solid var(--hairline)" }}>
                  <td className="py-2.5 pr-3 font-mono" style={{ fontWeight: 600, color: "var(--ink-1)" }}>
                    {r.tag}
                    <span style={{ color: "var(--ink-3)", fontWeight: 400 }}> · {r.asset_class}</span>
                  </td>
                  <td className="py-2.5 pr-3 font-mono" style={{ color: "var(--ink-2)" }}>{r.authority || "—"}</td>
                  <td className="py-2.5 pr-3 font-mono tabular-nums" style={{ color: "var(--ink-2)" }}>
                    {r.interval_months ? `${(r.interval_months / 12).toFixed(r.interval_months % 12 ? 1 : 0)} yr` : "—"}
                    {r.interval_months && (
                      <span title={r.basis === "confirmed" ? `ยืนยันโดย ${r.confirmed_by} · ${r.confirmed_at}` : "ค่าทั่วไป — ยังไม่ยืนยัน"}
                            style={{ marginLeft: 6, fontSize: 9, letterSpacing: ".06em",
                                     color: r.basis === "confirmed" ? OK : WARN }}>
                        {r.basis === "confirmed" ? "✓ CONFIRMED" : "~ INFERRED"}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 font-mono tabular-nums" style={{ color: "var(--ink-3)" }}>{r.last_inspection || "—"}</td>
                  <td className="py-2.5 pr-3 font-mono tabular-nums" style={{ color: r.state === "overdue" ? CRIT : "var(--ink-1)" }}>
                    {r.next_due || "—"}
                    {r.next_due && r.provisional && <span style={{ color: WARN, fontSize: 9 }}> *</span>}
                  </td>
                  <td className="py-2.5 font-mono uppercase" style={{ fontSize: 9.5, letterSpacing: ".1em", color: m.color, fontWeight: 700 }}>
                    {m.label}
                    {r.months_remaining !== null && r.state !== "unknown" && (
                      <span style={{ color: "var(--ink-3)", fontWeight: 400, letterSpacing: 0 }}>
                        {" "}{r.months_remaining < 0 ? `${Math.abs(Math.round(r.months_remaining))} mo ago` : `${Math.round(r.months_remaining)} mo`}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="font-mono mt-3" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
          * due-date จาก inferred interval — provisional จนกว่าผู้มีคุณวุฒิจะยืนยันกับ permit · หลักการ "ไม่เดา"
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }): JSX.Element {
  return (
    <div className="px-3 py-2" style={{ border: "1px solid var(--hairline)", borderRadius: 8, minWidth: 92, background: "#fff" }}>
      <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".14em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-mono tabular-nums" style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    </div>
  );
}
function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="text-left pb-2 pr-3 font-normal">{children}</th>;
}
function Center({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="absolute inset-0 flex items-center justify-center font-mono text-xs" style={{ color: "var(--ink-3)" }}>{children}</div>;
}
